import type { InteractiveAttempt } from './TaskAttempt';
// Game controller: owns the save state and applies every rule from the GDD.
import {
  CELLAR,
  ENERGY,
  MASTERY_XP,
  MILK,
  RICOTTA,
  SUPPLIES,
  TASK,
  TASK_IDS,
  TIME,
  VAT,
  XP,
  MARKET,
  type SupplyId,
  type TaskId,
} from '../config/constants';
import { CHEESES, type CheeseDefinition } from '../data/cheeses';
import { CODEX_BY_CHEESE, CODEX_ENTRIES } from '../data/codex';
import { SKILLS, SKILL_BY_ID } from '../data/skills';
import { COIN_UPGRADE_BY_ID, upgradeCost, upgradeLevel, type CoinUpgradeId } from '../data/coinUpgrades';
import * as B from '../process/Batch';
import * as Cellar from './Cellar';
import * as Codex from './Codex';
import { Emitter } from './events';
import {
  absMinute,
  createNewState,
  loadState,
  saveState,
  clearSave,
  type Employee,
  type FeedType,
  type InventoryCheese,
  type InventoryMilk,
  type InventoryWhey,
  type RoomId,
  type SaveState,
  type StaffStation,
} from './GameState';
import { cheeseUnitPrice, quoteCheeseSale, rollGems, wheyPricePerGallon, type MarketSnapshot, type PriceBreakdown } from './Market';
import { settleSaleAmount } from '../data/pricing';
import { advanceStaffJobs } from './StaffProduction';
import { clearStaffNavigation, ensureStaffPosition } from './StaffNavigation';
import * as Staff from './Staff';

export type ToastKind = 'info' | 'good' | 'bad' | 'gem';
export type Channel = 'wholesale' | 'retail';
export type BatchSlot = 'vat' | 'table' | 'tub';

type GameEvents = {
  change: void;
  toast: { text: string; kind: ToastKind };
  day: number;
  room: RoomId;
  batchStage: B.Stage | null;
};

export interface MilkResult {
  gallons: number;
  quality: number;
  cream: number;
}

export class Game extends Emitter<GameEvents> {
  state: SaveState;
  snapshot: MarketSnapshot;
  /** Set by the UI while a task panel is open (Focus Mode) */
  focus = false;
  /** Set by the UI while the Codex is open: the clock stops for reading */
  paused = false;
  /** Developer multiplier for testing */
  devTimeScale = 1;
  /** Live quiz attempt (not saved; closing the book forfeits it) */
  quiz: Codex.QuizAttempt | null = null;
  rng: () => number = Math.random;
  private autosaveAcc = 0;

  constructor(snapshot: MarketSnapshot) {
    super();
    this.snapshot = snapshot;
    this.state = loadState() ?? createNewState();
    this.state.playerMovingTo = null;
    for (const employee of this.state.staff.employees) ensureStaffPosition(this.state, employee, true);
    this.state.market.snapshotDate = snapshot.generatedAt;
  }

  // ---------------------------------------------------------------- time

  tick(realDeltaSec: number): void {
    if (realDeltaSec <= 0) return;
    if (!this.paused) {
      const scale = (this.focus ? TIME.FOCUS_TIME_SCALE : 1) * this.devTimeScale;
      const minutes = (realDeltaSec / TIME.REAL_SECONDS_PER_GAME_MINUTE) * scale;
      this.advanceMinutes(minutes);
    }
    this.autosaveAcc += realDeltaSec;
    if (this.autosaveAcc >= TIME.AUTOSAVE_REAL_SECONDS) {
      this.autosaveAcc = 0;
      this.save();
    }
  }

  get workDayEnd(): number {
    return TIME.DAY_START + TIME.WORK_MINUTES;
  }
  get forcedSleepAt(): number {
    return this.workDayEnd + TIME.OVERTIME_MINUTES;
  }
  get isWorkDayOver(): boolean {
    return this.state.minuteOfDay >= this.workDayEnd;
  }
  get now(): number {
    return absMinute(this.state);
  }

  advanceMinutes(minutes: number): void {
    if (minutes <= 0) return;
    const s = this.state;
    const before = s.minuteOfDay;
    const after = Math.min(before + minutes, this.forcedSleepAt);
    const advanced = after - before;
    s.minuteOfDay = after;

    // Passive energy drain, doubled in overtime
    const normal = Math.max(0, Math.min(after, this.workDayEnd) - Math.min(before, this.workDayEnd));
    const overtime = Math.max(0, after - Math.max(before, this.workDayEnd));
    const drain = (normal / 60) * ENERGY.PASSIVE_DRAIN_PER_HOUR + (overtime / 60) * ENERGY.PASSIVE_DRAIN_PER_HOUR * ENERGY.OVERTIME_DRAIN_MULT;
    s.energy = this.devMode ? ENERGY.MAX : Math.max(0, s.energy - drain);

    if (advanced > 0) {
      for (const slot of ['vat', 'table', 'tub'] as BatchSlot[]) {
        const b = s[slot].batch;
        if (!b) continue;
        const stageBefore = b.stage;
        B.tickBatch(b, advanced, this.now);
        if (stageBefore !== b.stage) this.emit('batchStage', b.stage);
      }
      advanceStaffJobs(s, advanced, { rng: this.rng, recipeBlock: id => this.recipeBlock(id), toast: message => this.toast(message, 'info') });
    }

    if (s.minuteOfDay >= this.forcedSleepAt) {
      this.toast('18:00. You collapse into bed.', 'bad');
      this.endDay(true);
      return;
    }
    this.emit('change', undefined);
  }

  // -------------------------------------------------------------- energy

  /** Returns a reason string if the task cannot start, else null. */
  canStartTask(task: TaskId): string | null {
    if (this.devMode) return null;
    if (this.state.energy <= 0) return 'No energy left. Eat some cheese or end the day.';
    void task;
    return null;
  }

  get lowEnergy(): boolean {
    return this.state.energy < ENERGY.LOW_THRESHOLD;
  }

  /** Multiplier applied to every minigame window for a task. */
  toleranceMult(task: TaskId): number {
    const lvl = this.taskLevel(task);
    let m = 1 + Math.min(XP.TOLERANCE_CAP, XP.TOLERANCE_PER_LEVEL * lvl);
    if (this.hasSkill('steady_hands_1')) m += XP.STEADY_HANDS_BONUS;
    m += 0.01 * upgradeLevel(this.state, 'steady_paws');
    if (this.lowEnergy) m *= ENERGY.LOW_TOLERANCE_MULT;
    if (this.state.settings.relaxedTiming) m *= XP.RELAXED_TIMING_MULT;
    return m;
  }

  private spendTask(task: TaskId, xp: number, batch?: B.Batch, effortOverride?: number): void {
    const def = TASK[task];
    if (!this.devMode) this.state.energy = Math.max(0, this.state.energy - def.energy * (1 - 0.01 * (effortOverride ?? batch?.coinUpgradeSnapshot?.efficient_effort ?? upgradeLevel(this.state, 'efficient_effort'))));
    this.addXp(task, xp, batch);
    this.tutorialComplete(task);
    if (def.minutes > 0) this.advanceMinutes(def.minutes);
    else this.emit('change', undefined);
  }

  // ---------------------------------------------------------------- xp

  /**
   * XP counts toward the task until mastery. After that it flows into the training
   * bank for that process, more slowly as the bank fills.
   */
  addXp(task: TaskId, amount: number, batch?: B.Batch): void {
    if (task !== 'sell' && task !== 'feed' && task !== 'milk' && task !== 'cellar') amount *= 1 + 0.02 * (batch?.coinUpgradeSnapshot?.craft_notebook ?? upgradeLevel(this.state, 'craft_notebook'));
    const a = Math.max(0, amount);
    if (a === 0) return;
    const s = this.state;
    const before = this.taskLevel(task);
    const room = Math.max(0, MASTERY_XP - s.xp.tasks[task]);
    const toTask = Math.min(a, room);
    const overflow = a - toTask;
    s.xp.tasks[task] += toTask;
    s.xp.total += a;
    if (overflow > 0) {
      const bank = s.xp.bank[task] ?? 0;
      const gain = overflow / (1 + bank / XP.BANK_SOFT_CAP);
      s.xp.bank[task] = Math.round((bank + gain) * 10) / 10;
    }
    const after = this.taskLevel(task);
    if (after > before) {
      this.toast(after >= XP.MASTERY_LEVEL ? `${TASK[task].label}: mastery! Extra XP now banks for training.` : `${TASK[task].label} level ${after}!`, 'good');
    }
  }

  taskLevel(task: TaskId): number {
    return Math.floor(Math.sqrt(this.state.xp.tasks[task] / XP.LEVEL_DIVISOR));
  }

  isMastered(task: TaskId): boolean {
    return this.state.xp.tasks[task] >= MASTERY_XP;
  }

  /** 0..1 progress toward mastery */
  masteryProgress(task: TaskId): number {
    return Math.min(1, this.state.xp.tasks[task] / MASTERY_XP);
  }

  get overallLevel(): number {
    return Math.floor(Math.sqrt(this.state.xp.total / XP.LEVEL_DIVISOR));
  }

  // ------------------------------------------------------------- bessie

  moveBessie(location: 'barn' | 'pasture'): boolean {
    if (location !== 'barn' && location !== 'pasture') return false;
    if (location === 'pasture' && !this.hasSkill('field')) { this.toast('Unlock Sunny Pasture in Farm Milestones first.', 'info'); return false; }
    if (Object.keys(this.state.interactiveAttempts).some(k => k.startsWith('milk:')) || this.state.staff.employees.some(e => e.job?.kind === 'milk' && e.job.phase === 'work')) {
      this.toast('Finish the current milking before moving Bessie.', 'info'); return false;
    }
    if (this.state.bessie.location === location) return false;
    this.state.bessie.location = location;
    this.toast(location === 'pasture' ? 'Bessie is grazing in Sunny Pasture. Milk yield +15%!' : 'Bessie is back in the barn.', 'good');
    this.save(); this.emit('change', undefined); return true;
  }

  feedBessie(type: FeedType): boolean {
    const s = this.state;
    const supply: SupplyId = type === 'basic' ? 'feed_basic' : 'feed_quality';
    if (s.bessie.fedToday) {
      this.toast('Bessie has already eaten today.', 'info');
      return false;
    }
    if (s.supplies[supply] <= 0) {
      this.toast(`No ${SUPPLIES[supply].label.toLowerCase()} left. Buy some at the supply desk.`, 'bad');
      return false;
    }
    const reason = this.canStartTask('feed');
    if (reason) {
      this.toast(reason, 'bad');
      return false;
    }
    s.supplies[supply]--;
    s.bessie.fedToday = true;
    s.bessie.feedType = type;
    this.toast(`Bessie munches the ${type} feed. Moo.`, 'good');
    this.spendTask('feed', 2);
    return true;
  }

  milkCooldownRemaining(): number {
    const last = this.state.bessie.lastMilkedMinute;
    if (last === null) return 0;
    return Math.max(0, MILK.COOLDOWN_MINUTES - (this.now - last));
  }

  canMilk(): string | null {
    if (!this.state.bessie.fedToday) return 'Bessie is hungry. Feed her first.';
    const cd = this.milkCooldownRemaining();
    if (cd > 0) return `Bessie needs ${Math.ceil(cd)} more minutes before the next milking.`;
    return this.canStartTask('milk');
  }

  /** Called when the milking minigame finishes. `score` is 0..1. */
  finishMilking(score: number, reservation?: InteractiveAttempt['milk']): MilkResult {
    const s = this.state;
    const feed = MILK.FEED[reservation?.feedType ?? s.bessie.feedType ?? 'basic'];
    const level = this.taskLevel('milk');
    const base = MILK.YIELD_MIN + this.rng() * (MILK.YIELD_MAX - MILK.YIELD_MIN);
    const fieldYield = (reservation?.pasture ?? (s.bessie.location === 'pasture' && this.hasSkill('field'))) ? 1.15 : 1;
    const gallons = Math.round((base * (MILK.SCORE_YIELD_BASE + MILK.SCORE_YIELD_RANGE * score) + MILK.LEVEL_YIELD_BONUS * level) * fieldYield * (1 + 0.01 * (reservation?.careLevel ?? upgradeLevel(s, 'bessie_care'))) * 10) / 10;
    const quality = Math.min(5, feed.qualityBase + MILK.SCORE_QUALITY_BONUS * score);
    const cream = Math.round((feed.cream + (this.rng() * 2 - 1) * feed.creamVar) * 10) / 10;
    const item: InventoryMilk = { kind: 'milk', qty: gallons, quality, cream, madeMinute: this.now };
    s.inventory.push(item);
    if (!reservation) { s.bessie.lastMilkedMinute = this.now; s.bessie.milkingsToday++; }
    this.toast(`${gallons} gal of milk, quality ${quality.toFixed(1)}, ${cream}% cream.`, 'good');
    this.spendTask('milk', 20 * (0.5 + score), undefined, reservation?.effortLevel);
    return { gallons, quality, cream };
  }

  // ------------------------------------------------------------ recipes

  /** Why the farm cannot run this cheese's process right now, or null. */
  recipeBlock(cheeseId: string): string | null {
    const def = CHEESES[cheeseId];
    if (!def || !def.process) return 'Not makeable in this build.';
    if (!Codex.isLearned(this.state, cheeseId)) return `Learn ${def.name} in the Codex first (discover it and pass the quiz).`;
    for (const sk of def.requiresSkills) if (!this.hasSkill(sk)) return `Requires the ${SKILL_BY_ID[sk]?.name ?? sk}.`;
    return null;
  }

  /** Cheeses the player may start at the vat. */
  availableRecipes(): CheeseDefinition[] {
    return Object.values(CHEESES).filter((d) => d.process && Codex.isLearned(this.state, d.id));
  }

  // ---------------------------------------------------------------- vat

  get batch(): B.Batch | null {
    return this.state.vat.batch;
  }

  batchAt(slot: BatchSlot): B.Batch | null {
    return this.state[slot].batch;
  }

  get vatWorn(): boolean {
    return this.state.vat.durability < VAT.WORN_THRESHOLD;
  }

  milkStacks(): { index: number; item: InventoryMilk }[] {
    return this.state.inventory
      .map((item, index) => ({ index, item }))
      .filter((e): e is { index: number; item: InventoryMilk } => e.item.kind === 'milk');
  }

  wheyStack(): { index: number; item: InventoryWhey } | null {
    const index = this.state.inventory.findIndex((i) => i.kind === 'whey');
    if (index < 0) return null;
    return { index, item: this.state.inventory[index] as InventoryWhey };
  }

  /** Pour a milk stack into the vat for a milk-based process. */
  startBatch(inventoryIndex: number, process: B.Process = 'mozzarella'): boolean {
    const s = this.state;
    const item = s.inventory[inventoryIndex];
    if (!item || item.kind !== 'milk') return false;
    if (process === 'ricotta') return false;
    const block = this.recipeBlock(B.PROCESS_CHEESE[process]);
    if (block) {
      this.toast(block, 'bad');
      return false;
    }
    if (s.vat.batch) {
      this.toast('The vat is busy.', 'bad');
      return false;
    }
    const gallons = Math.min(item.qty, MILK.VAT_CAPACITY_GAL);
    const batch = B.createBatch(process, gallons, item.quality, item.cream, this.now, 'player', 'Player', this.hasTech('stardust_precision_matrix'));
    if (this.vatWorn) {
      B.penalize(batch, this.now, VAT.WORN_PENALTY, 'Worn vat');
      this.toast('The vat is worn. Repair it at the supply desk to stop losing quality.', 'bad');
    }
    item.qty = Math.round((item.qty - gallons) * 10) / 10;
    if (item.qty <= 0) s.inventory.splice(inventoryIndex, 1);
    batch.coinUpgradeSnapshot = { ...s.coinUpgradeLevels };
    s.vat.batch = batch;
    this.emit('batchStage', batch.stage);
    this.emit('change', undefined);
    return true;
  }

  /** Pour whey into the vat for ricotta. */
  startRicotta(gallons?: number): boolean {
    const s = this.state;
    const block = this.recipeBlock('ricotta');
    if (block) {
      this.toast(block, 'bad');
      return false;
    }
    if (s.vat.batch) {
      this.toast('The vat is busy.', 'bad');
      return false;
    }
    const whey = this.wheyStack();
    if (!whey || whey.item.qty < RICOTTA.MIN_GALLONS) {
      this.toast(`Ricotta needs at least ${RICOTTA.MIN_GALLONS} gallons of whey.`, 'bad');
      return false;
    }
    const gal = Math.round(Math.min(gallons ?? MILK.VAT_CAPACITY_GAL, whey.item.qty, MILK.VAT_CAPACITY_GAL) * 10) / 10;
    const batch = B.createBatch('ricotta', gal, whey.item.quality, 0, this.now, 'player', 'Player', this.hasTech('stardust_precision_matrix'));
    if (this.vatWorn) {
      B.penalize(batch, this.now, VAT.WORN_PENALTY, 'Worn vat');
      this.toast('The vat is worn. Repair it at the supply desk to stop losing quality.', 'bad');
    }
    whey.item.qty = Math.round((whey.item.qty - gal) * 10) / 10;
    if (whey.item.qty <= 0) s.inventory.splice(whey.index, 1);
    batch.coinUpgradeSnapshot = { ...s.coinUpgradeLevels };
    s.vat.batch = batch;
    this.emit('batchStage', batch.stage);
    this.emit('change', undefined);
    return true;
  }

  setBurner(burner: B.Burner): void {
    if (!this.batch) return;
    B.setBurner(this.batch, burner);
    this.emit('change', undefined);
  }

  pullPasteurized(): void {
    const b = this.batch;
    if (!b) return;
    const penalized = b.scorchedMinutes > 0 || b.overheldMinutes > 0;
    if (B.pullPasteurized(b, this.now)) {
      this.spendTask('pasteurize', penalized ? 10 : 15, b);
      this.emit('batchStage', b.stage);
    }
  }

  setCooling(on: boolean): void {
    if (!this.batch) return;
    B.setCooling(this.batch, on);
    this.emit('change', undefined);
  }

  private useSupply(id: SupplyId, task: TaskId): boolean {
    if (this.state.supplies[id] <= 0) {
      this.toast(`No ${SUPPLIES[id].label.toLowerCase()}. Buy some at the supply desk.`, 'bad');
      return false;
    }
    const reason = this.canStartTask(task);
    if (reason) {
      this.toast(reason, 'bad');
      return false;
    }
    return true;
  }

  // ---- mozzarella

  addCitricAcid(): boolean {
    const b = this.batch;
    if (!b || !this.useSupply('citric_acid', 'curdle')) return false;
    if (!B.addCitricAcid(b, this.now)) return false;
    this.state.supplies.citric_acid--;
    this.emit('change', undefined);
    return true;
  }

  finishCurdleStir(hits: number, misses: number): void {
    const b = this.batch;
    if (!b) return;
    if (B.finishCurdleStir(b, this.now, misses)) {
      const ratio = hits / Math.max(1, hits + misses);
      this.spendTask('curdle', 15 * ratio, b);
      this.emit('batchStage', b.stage);
    }
  }

  addRennet(): boolean {
    const b = this.batch;
    if (!b || !this.useSupply('rennet', 'rennet')) return false;
    if (!B.addRennet(b)) return false;
    this.state.supplies.rennet--;
    this.emit('change', undefined);
    return true;
  }

  finishRennetStir(): void {
    const b = this.batch;
    if (!b) return;
    if (B.finishRennetStir(b, this.now)) this.emit('change', undefined);
  }

  checkSet(): void {
    const b = this.batch;
    if (!b || b.process === 'taleggio') return;
    if (B.checkSet(b, this.now)) {
      this.spendTask('rennet', 10, b);
      this.emit('batchStage', b.stage);
    }
  }

  slice(slot: BatchSlot = 'vat'): void {
    const b = this.batchAt(slot);
    if (!b) return;
    if (B.slice(b)) this.emit('change', undefined);
  }

  drainWhey(): void {
    const b = this.batch;
    if (!b) return;
    const whey = B.drain(b);
    if (whey > 0) {
      this.addWhey(whey, b.quality);
      this.toast(`${whey} gal of whey drained into the whey can.`, 'info');
    }
    this.emit('change', undefined);
  }

  finishFirming(earlyClicks: number): void {
    const b = this.batch;
    if (!b) return;
    if (B.finishFirming(b, this.now, earlyClicks)) {
      this.spendTask('cut', 10, b);
      this.emit('batchStage', b.stage);
    }
  }

  setHeating(on: boolean): void {
    if (!this.batch) return;
    B.setHeating(this.batch, on);
    this.emit('change', undefined);
  }

  finishStretching(hits: number, misses: number): void {
    const b = this.batch;
    if (!b) return;
    if (B.finishStretching(b, this.now, misses, misses === 0)) {
      const balls = B.formBalls(b, this.now);
      this.wearVat(b);
      this.state.stats.batches++;
      const ratio = hits / Math.max(1, hits + misses);
      this.spendTask('stretch', 15 * ratio, b);
      this.toast(`Formed ${balls} ball${balls === 1 ? '' : 's'} of ${B.stars(b)}★ mozzarella.`, 'good');
      this.emit('batchStage', b.stage);
    }
  }

  /** Package finished units from the vat (mozzarella balls or ricotta tubs). Returns the number packed. */
  bagBatch(): number {
    const b = this.batch;
    const s = this.state;
    if (!b || (b.stage !== 'bag' && b.stage !== 'tub')) return 0;
    const reason = this.canStartTask('bag');
    if (reason) {
      this.toast(reason, 'bad');
      return 0;
    }
    const n = Math.min(b.units, s.supplies.packaging);
    if (n <= 0) {
      this.toast(b.units > 0 ? 'No packaging left. Buy some at the supply desk.' : 'Nothing to pack.', 'bad');
      if (b.units === 0) this.clearSlot('vat');
      return 0;
    }
    const def = CHEESES[B.PROCESS_CHEESE[b.process]];
    s.supplies.packaging -= n;
    b.units -= n;
    this.addCheese(def.id, n, B.stars(b));
    this.state.learning.madeCheese = true;
    this.spendTask('bag', 5, b);
    this.toast(`Packed ${n} ${def.unitLabel}${n === 1 ? '' : 's'} of ${def.name}.`, 'good');
    if (b.units === 0) this.clearSlot('vat');
    return n;
  }

  eatUnbagged(): void {
    const b = this.batch;
    if (!b || (b.stage !== 'bag' && b.stage !== 'tub') || b.units <= 0) return;
    const def = CHEESES[B.PROCESS_CHEESE[b.process]];
    b.units--;
    this.restoreEnergy(def.energyRestore.base + def.energyRestore.perStar * B.stars(b));
    if (b.units === 0) this.clearSlot('vat');
    this.emit('change', undefined);
  }

  discardBatch(slot: BatchSlot = 'vat'): void {
    if (!this.batchAt(slot)) return;
    this.toast('Batch discarded.', 'bad');
    this.clearSlot(slot);
  }

  private clearSlot(slot: BatchSlot): void {
    this.state[slot].batch = null;
    this.emit('batchStage', null);
    this.emit('change', undefined);
  }

  private wearVat(batch?: B.Batch): void {
    this.state.vat.durability = Math.max(0, this.state.vat.durability - VAT.WEAR_PER_BATCH * (1 - 0.02 * (batch?.coinUpgradeSnapshot?.sturdy_vat ?? upgradeLevel(this.state, 'sturdy_vat'))));
  }

  // ---- ricotta

  addRicottaAcid(): boolean {
    const b = this.batch;
    if (!b || b.process !== 'ricotta' || !this.useSupply('citric_acid', 'curdle')) return false;
    const scorched = b.scorchedMinutes > 0;
    if (!B.addRicottaAcid(b, this.now)) return false;
    this.state.supplies.citric_acid--;
    this.addXp('pasteurize', scorched ? 5 : 10);
    this.spendTask('curdle', 10, b);
    this.emit('batchStage', b.stage);
    return true;
  }

  startSkim(): void {
    const b = this.batch;
    if (!b || b.process !== 'ricotta') return;
    if (B.startSkim(b, this.now)) {
      this.emit('batchStage', b.stage);
      this.emit('change', undefined);
    }
  }

  finishSkim(hits: number, misses: number): void {
    const b = this.batch;
    if (!b) return;
    if (B.finishSkim(b, this.now, misses)) {
      const ratio = hits / Math.max(1, hits + misses);
      this.spendTask('cut', 10 * ratio, b);
      this.emit('batchStage', b.stage);
    }
  }

  finishRicottaDrain(): void {
    const b = this.batch;
    if (!b || !B.canTub(b, this.now)) return;
    const units = B.finishDrain(b, this.now);
    this.wearVat(b);
    this.state.stats.batches++;
    this.toast(`${units} tub${units === 1 ? '' : 's'} of ${B.stars(b)}★ ricotta ready to pack.`, 'good');
    this.emit('batchStage', b.stage);
    this.emit('change', undefined);
  }

  // ---- taleggio

  addCulture(): boolean {
    const b = this.batch;
    if (!b || b.process !== 'taleggio' || !this.useSupply('culture', 'culture')) return false;
    if (!B.addCulture(b, this.now)) return false;
    this.state.supplies.culture--;
    this.emit('change', undefined);
    return true;
  }

  checkRipening(): void {
    const b = this.batch;
    if (!b) return;
    if (B.checkRipening(b, this.now)) {
      this.spendTask('culture', 10, b);
      this.emit('batchStage', b.stage);
    }
  }

  /** Move the set taleggio curd from the vat to the draining table. */
  carryToTable(): boolean {
    const b = this.batch;
    if (!b || b.process !== 'taleggio') return false;
    if (this.state.table.batch) {
      this.toast('The draining table is busy.', 'bad');
      return false;
    }
    if (!B.carryToTable(b, this.now)) return false;
    this.state.table.batch = b;
    this.state.vat.batch = null;
    this.spendTask('rennet', 10, b);
    this.emit('batchStage', b.stage);
    return true;
  }

  fillMolds(): void {
    const b = this.state.table.batch;
    if (!b) return;
    const reason = this.canStartTask('cut');
    if (reason) {
      this.toast(reason, 'bad');
      return;
    }
    const whey = B.fillMolds(b, this.now);
    if (whey > 0) {
      this.addWhey(whey, b.quality);
      this.wearVat(b);
      this.spendTask('cut', 10, b);
      this.toast(`Molds filled. ${whey} gal of whey to the can. Turn them every 20 minutes.`, 'info');
    }
    this.emit('change', undefined);
  }

  turnMolds(): void {
    const b = this.state.table.batch;
    if (!b) return;
    if (B.turnMolds(b, this.now)) {
      this.addXp('cut', 2, b);
      this.toast('Molds turned.', 'info');
    }
    this.emit('change', undefined);
  }

  carryToTub(): boolean {
    const b = this.state.table.batch;
    if (!b) return false;
    if (this.state.tub.batch) {
      this.toast('The brine tub is busy.', 'bad');
      return false;
    }
    if (!B.carryToTub(b, this.now)) return false;
    this.state.tub.batch = b;
    this.state.table.batch = null;
    this.emit('batchStage', b.stage);
    this.emit('change', undefined);
    return true;
  }

  submerge(): boolean {
    const b = this.state.tub.batch;
    if (!b || !this.useSupply('salt', 'brine')) return false;
    if (!B.submerge(b, this.now)) return false;
    this.state.supplies.salt--;
    this.spendTask('brine', 5, b);
    return true;
  }

  pullFromBrine(): void {
    const b = this.state.tub.batch;
    if (!b) return;
    const units = B.pullFromBrine(b, this.now);
    if (units >= 0 && b.stage === 'rack') {
      this.state.stats.batches++;
      this.addXp('brine', 5);
      this.toast(`${units} square${units === 1 ? '' : 's'} of ${B.stars(b)}★ taleggio out of the brine. Carry them to the cellar.`, 'good');
      this.emit('batchStage', b.stage);
      this.emit('change', undefined);
    }
  }

  /** Carry the brined squares from the tub onto the cellar racks. */
  rackSquares(): boolean {
    const b = this.state.tub.batch;
    if (!b || b.stage !== 'rack') return false;
    if (!this.hasSkill('cellar')) {
      this.toast('You need the Cellar to age cheese.', 'bad');
      return false;
    }
    if (b.units <= 0) {
      this.clearSlot('tub');
      return false;
    }
    if (Cellar.rackSpace(this.state) < b.units) {
      this.toast(`Only ${Cellar.rackSpace(this.state)} rack spaces free.`, 'bad');
      return false;
    }
    const reason = this.canStartTask('cellar');
    if (reason) {
      this.toast(reason, 'bad');
      return false;
    }
    Cellar.rackWheels(this.state, B.PROCESS_CHEESE[b.process], b.units, B.stars(b));
    this.state.learning.madeCheese = true;
    this.toast(`${b.units} square${b.units === 1 ? '' : 's'} on the racks. Wash the rinds every 5 days.`, 'good');
    this.state.tub.batch = null;
    this.spendTask('cellar', 8);
    this.emit('batchStage', null);
    return true;
  }

  // ------------------------------------------------------------- cellar

  setCellarDials(temp: number, humidity: number): void {
    const c = this.state.cellar;
    c.temp = Math.round(Math.min(75, Math.max(35, temp)) * 10) / 10;
    c.humidity = Math.round(Math.min(100, Math.max(40, humidity)) * 10) / 10;
    this.emit('change', undefined);
  }

  washRinds(): number {
    const s = this.state;
    const due = Cellar.stacksNeedingWash(s).length;
    if (due === 0) {
      this.toast('No rinds need washing yet.', 'info');
      return 0;
    }
    if (!this.useSupply('salt', 'cellar')) return 0;
    s.supplies.salt -= CELLAR.SALT_PER_WASH;
    const n = Cellar.washDue(s);
    this.spendTask('cellar', 8 + 2 * n);
    this.toast(`Washed ${n} rack${n === 1 ? '' : 's'} with brine.`, 'good');
    return n;
  }

  /** Wrap wheels and bring them to inventory. Uses packaging per unit. */
  takeOutWheels(stackId: string, qty: number): boolean {
    if (!Number.isSafeInteger(qty) || qty <= 0) return false;
    const s = this.state;
    const stack = s.cellar.racks.find((r) => r.id === stackId);
    if (!stack) return false;
    const n = Math.min(qty, stack.qty, s.supplies.packaging);
    if (n <= 0) {
      this.toast('No packaging to wrap them in.', 'bad');
      return false;
    }
    const item = Cellar.takeOut(s, stackId, n);
    if (!item) return false;
    s.supplies.packaging -= n;
    const def = CHEESES[item.cheeseId];
    if ((!stack.ownerId || stack.ownerId === 'player') && stack.ageDays >= (Cellar.ageRule(stack.cheeseId)?.minAgeDays ?? Infinity)) s.learning.agedCheese = true;
    this.toast(`Wrapped ${n} ${def.unitLabel}${n === 1 ? '' : 's'} of ${def.name} at ×${(item.agedMult ?? 1).toFixed(2)} value.`, 'good');
    this.addXp('bag', 3 * n);
    this.emit('change', undefined);
    return true;
  }

  // -------------------------------------------------------------- codex

  discoverEntry(cheeseId: string): boolean {
    const block = Codex.discoveryBlock(this.state, cheeseId);
    if (block) {
      this.toast(block, 'bad');
      return false;
    }
    const fee = Codex.discoveryFee(cheeseId);
    this.state.gold = Math.round((this.state.gold - fee) * 100) / 100;
    this.state.codex.discovered.push(cheeseId);
    this.toast(`Discovered ${CHEESES[cheeseId].name} for ${fee} gold. Read the entry, then take the quiz.`, 'good');
    this.emit('change', undefined);
    return true;
  }

  startQuiz(cheeseId: string): Codex.QuizAttempt | null {
    const block = Codex.quizBlock(this.state, cheeseId);
    if (block) {
      this.toast(block, 'bad');
      return null;
    }
    const entry = CODEX_BY_CHEESE[cheeseId];
    const cost = Codex.quizCost(this.state, cheeseId);
    this.state.gold = Math.round((this.state.gold - cost) * 100) / 100;
    this.state.codex.attempts[cheeseId] = (this.state.codex.attempts[cheeseId] ?? 0) + 1;
    this.quiz = Codex.buildAttempt(entry, this.rng);
    if (cost > 0) this.toast(`Retest: ${cost} gold.`, 'info');
    this.emit('change', undefined);
    return this.quiz;
  }

  answerQuiz(optionIndex: number): { correct: boolean; gems: number } {
    const a = this.quiz;
    if (!a) return { correct: false, gems: 0 };
    const r = Codex.answerCurrent(this.state, a, optionIndex);
    if (r.gems > 0) {
      this.state.gems += r.gems;
      this.state.stats.gemsFound += r.gems;
      this.toast(`Correct! +${r.gems} cosmic gem.`, 'gem');
    }
    this.emit('change', undefined);
    return r;
  }

  nextQuizQuestion(): void {
    const a = this.quiz;
    if (!a) return;
    const wasLearned = Codex.isLearned(this.state, a.cheeseId);
    Codex.advanceAttempt(this.state, a);
    if (a.done) {
      const name = CHEESES[a.cheeseId].name;
      if (a.passed && !wasLearned) this.toast(`You learned ${name}! The recipe is yours.`, 'good');
      else if (a.passed) this.toast(`${name}: ${a.correct}/${a.questions.length}.`, 'good');
      else this.toast(`${name}: ${a.correct}/${a.questions.length}. Read the entry again and retest.`, 'bad');
    }
    this.emit('change', undefined);
  }

  abandonQuiz(): void {
    this.quiz = null;
  }

  // -------------------------------------------------------------- staff

  get hasStaff(): boolean {
    return this.hasSkill('employees') && this.state.learning.madeCheese && this.state.learning.agedCheese && this.state.learning.soldCheese;
  }

  hasTech(id: string): boolean {
    return this.state.skills.includes(id);
  }

  buyTech(id: 'stardust_precision_matrix' | 'anti_gravity_milker'): boolean {
    return this.buySkill(id);
  }

  /** Package ageable squares immediately instead of sending them to the cellar. */
  packageTaleggioUnaged(): number {
    const b = this.state.tub.batch;
    if (!b || b.process !== 'taleggio' || b.stage !== 'rack' || b.units <= 0) return 0;
    const n = Math.min(b.units, this.state.supplies.packaging);
    if (n <= 0) {
      this.toast('No packaging left. Buy some at the supply desk.', 'bad');
      return 0;
    }
    this.state.supplies.packaging -= n;
    b.units -= n;
    this.addCheese('taleggio', n, B.stars(b));
    this.state.learning.madeCheese = true;
    this.toast(`Packed ${n} unaged Taleggio square${n === 1 ? '' : 's'}.`, 'good');
    if (b.units <= 0) this.clearSlot('tub');
    return n;
  }

  candidates(): Employee[] {
    if (!this.hasStaff) return [];
    if (Staff.candidatesStale(this.state)) Staff.refreshCandidates(this.state, this.rng);
    return this.state.staff.candidates;
  }

  hire(candidateId: string): boolean {
    const s = this.state;
    if (!this.hasSkill('employees')) {
      this.toast('Unlock Employees in the skill tree first.', 'bad');
      return false;
    }
    if (!s.learning.madeCheese || !s.learning.agedCheese || !s.learning.soldCheese) {
      this.toast('Before hiring, make, age, and sell a cheese yourself.', 'bad');
      return false;
    }
    const i = s.staff.candidates.findIndex((c) => c.id === candidateId);
    if (i < 0) return false;
    const e = s.staff.candidates[i];
    const fee = Staff.signingFee(e);
    if (s.gold < fee) {
      this.toast(`Signing fee is ${fee.toFixed(2)} gold ( days' wages).`, 'bad');
      return false;
    }
    s.gold = Math.round((s.gold - fee) * 100) / 100;
    s.staff.candidates.splice(i, 1);
    e.hiredDay = s.day;
    e.presentToday = true;
    s.staff.employees.push(e);
    ensureStaffPosition(s, e);
    this.toast(`${e.name} joins the farm at ${e.wage.toFixed(2)} gold a day.`, 'good');
    this.emit('change', undefined);
    return true;
  }

  dismiss(employeeId: string): void {
    const s = this.state;
    const i = s.staff.employees.findIndex((e) => e.id === employeeId);
    if (i < 0) return;
    if (s.staff.employees[i].job) {
      s.staff.employees[i].leaving = true;
      this.toast(`${s.staff.employees[i].name} will leave after delivering the current batch.`, 'info');
      this.emit('change', undefined);
      return;
    }
    const [e] = s.staff.employees.splice(i, 1);
    this.toast(`${e.name} packs up and leaves.`, 'info');
    this.emit('change', undefined);
  }

  assign(employeeId: string, station: StaffStation | null): void {
    const e = this.state.staff.employees.find((x) => x.id === employeeId);
    if (!e) return;
    if (e.leaving) return;
    if (station === 'cellar' && !this.hasSkill('cellar')) {
      this.toast('You need the Cellar first.', 'bad');
      return;
    }
    if (station && this.state.staff.employees.some(other => other !== e && (other.station === station || other.pendingStation === station))) {
      this.toast('Only one employee can be assigned to each station.', 'bad');
      return;
    }
    if (e.job) {
      e.pendingStation = station;
      this.toast(`${e.name} will change assignment after delivering the current batch.`, 'info');
      this.emit('change', undefined);
      return;
    }
    e.station = station;
    e.jobStatus = station ? 'walking' : 'idle';
    e.jobProgress = 0;
    e.jobLabel = station ? `Walking to ${Staff.STATION_LABEL[station]}` : '';
    e.blockedReason = null;
    clearStaffNavigation(e);
    ensureStaffPosition(this.state, e);
    this.emit('change', undefined);
  }

  setWage(employeeId: string, wage: number): void {
    const e = this.state.staff.employees.find((x) => x.id === employeeId);
    if (!e) return;
    e.wage = Math.max(1, Math.round(wage * 100) / 100);
    this.emit('change', undefined);
  }

  promote(employeeId: string, task: TaskId): boolean {
    const e = this.state.staff.employees.find((x) => x.id === employeeId);
    if (!e) return false;
    const block = Staff.canPromote(this.state, e, task);
    if (block) {
      this.toast(block, 'bad');
      return false;
    }
    e.trainerFor = task;
    e.wage = Staff.expectedWage(e);
    this.toast(`${e.name} is now the ${TASK[task].label.toLowerCase()} trainer at ${e.wage.toFixed(2)} gold a day.`, 'good');
    this.emit('change', undefined);
    return true;
  }

  setVatRecipe(cheeseId: string | null): void {
    if (cheeseId && this.recipeBlock(cheeseId)) return;
    this.state.staff.vatRecipe = cheeseId;
    this.emit('change', undefined);
  }


  private payWagesAndRollAttendance(): void {
    const s = this.state;
    const kept: Employee[] = [];
    for (const e of s.staff.employees) {
      if (e.leaving) { if (e.job) { e.presentToday = true; kept.push(e); } continue; }
      if (e.presentToday) {
        if (s.gold < e.wage) {
          this.toast(`You could not pay ${e.name}. They walk out.`, 'bad');
          if (e.job) { e.leaving = true; e.presentToday = true; kept.push(e); }
          continue;
        }
        s.gold = Math.round((s.gold - e.wage) * 100) / 100;
        s.stats.wagesPaid += e.wage;
        e.daysWorked++;
      } else {
        e.daysMissed++;
      }
      if (this.rng() < Staff.quitChance(e)) {
        this.toast(`${e.name} quit over pay (${e.wage.toFixed(2)} vs ${Staff.expectedWage(e).toFixed(2)} expected).`, 'bad');
        if (e.job) { e.leaving = true; e.presentToday = true; kept.push(e); }
        continue;
      }
      e.presentToday = this.rng() < Staff.attendanceChance(e);
      if (!e.presentToday) this.toast(`${e.name} will not show up tomorrow.`, 'bad');
      kept.push(e);
    }
    s.staff.employees = kept;
  }

  // ---------------------------------------------------------- inventory

  private addCheese(cheeseId: string, qty: number, stars: number, ownerId = 'player', ownerName = 'Player'): void {
    const s = this.state;
    const existing = s.inventory.find(
      (i): i is InventoryCheese => i.kind === 'cheese' && i.cheeseId === cheeseId && i.stars === stars && i.madeDay === s.day && !i.agedMult && (i.ownerId ?? 'player') === ownerId,
    );
    if (existing) existing.qty += qty;
    else s.inventory.push({ kind: 'cheese', cheeseId, qty, stars, madeDay: s.day, ownerId, ownerName });
  }

  private addWhey(gallons: number, quality: number): void {
    const s = this.state;
    const existing = s.inventory.find((i): i is InventoryWhey => i.kind === 'whey');
    if (existing) {
      const total = existing.qty + gallons;
      existing.quality = Math.round(((existing.quality * existing.qty + quality * gallons) / total) * 100) / 100;
      existing.qty = Math.round(total * 10) / 10;
    } else s.inventory.push({ kind: 'whey', qty: gallons, quality: Math.round(quality * 100) / 100 });
  }

  ageDays(item: InventoryCheese): number {
    return this.state.day - item.madeDay;
  }

  hasSkill(id: string): boolean {
    return this.state.skills.includes(id);
  }

  // ------------------------------------------------------------ dev mode

  /** Dev mode: everything unlocked for testing. Persists in the save. */
  get devMode(): boolean {
    return this.state.settings.devMode;
  }

  /**
   * Unlock everything: every skill (including later-build nodes), every Codex entry
   * discovered and learned, mastery in every process with a full training bank, deep
   * pockets, full supplies, the learning gates cleared, and the hiring board stocked.
   * Energy stays pinned at max while dev mode is on. Turning it off keeps what was
   * unlocked; "New game" starts clean.
   */
  enableDevMode(): void {
    const s = this.state;
    s.settings.devMode = true;
    for (const node of SKILLS) if (!s.skills.includes(node.id)) s.skills.push(node.id);
    for (const entry of CODEX_ENTRIES) {
      if (!s.codex.discovered.includes(entry.cheeseId)) s.codex.discovered.push(entry.cheeseId);
      if (!s.codex.learned.includes(entry.cheeseId)) s.codex.learned.push(entry.cheeseId);
    }
    for (const t of TASK_IDS) {
      s.xp.tasks[t] = Math.max(s.xp.tasks[t], MASTERY_XP);
      s.xp.bank[t] = Math.max(s.xp.bank[t] ?? 0, XP.BANK_SOFT_CAP);
    }
    s.xp.total = Math.max(s.xp.total, TASK_IDS.length * MASTERY_XP);
    s.gold = Math.max(s.gold, 100000);
    s.gems = Math.max(s.gems, 500);
    s.energy = ENERGY.MAX;
    for (const id of Object.keys(SUPPLIES) as SupplyId[]) s.supplies[id] = Math.max(s.supplies[id], 99);
    s.vat.durability = VAT.DURABILITY_MAX;
    s.learning.madeCheese = true;
    s.learning.agedCheese = true;
    s.learning.soldCheese = true;
    s.tutorial.done = true;
    Staff.refreshCandidates(s, this.rng);
    this.toast('Dev mode on: every skill, recipe, and process unlocked. Energy stays full.', 'gem');
    this.save();
    this.emit('room', s.room);
    this.emit('change', undefined);
  }

  disableDevMode(): void {
    this.state.settings.devMode = false;
    this.toast('Dev mode off. Unlocks stay; start a new game for a clean run.', 'info');
    this.emit('change', undefined);
  }

  get negotiation(): number {
    return 1 + (this.hasSkill('market_negotiation_1') ? MARKET.NEGOTIATION_PER_SKILL : 0) + 0.005 * upgradeLevel(this.state, 'merchants_ledger');
  }

  get hasShop(): boolean {
    return this.hasSkill('shop');
  }

  cheesePrice(item: InventoryCheese, channel: Channel): PriceBreakdown {
    return cheeseUnitPrice(this.state, this.snapshot, item.cheeseId, item.stars, this.ageDays(item), channel, this.negotiation, this.saleAging(item));
  }

  private saleAging(item: InventoryCheese): number {
    return (item.agedMult ?? 1) * (item.agedMult !== undefined && this.hasSkill('cellar_value_1') ? 1.1 : 1);
  }

  quoteSale(item: InventoryCheese, qty: number, channel: Channel): number {
    if (!Number.isFinite(qty) || qty <= 0) return 0;
    const n = Math.min(Math.floor(qty), item.qty);
    const raw = quoteCheeseSale(this.state, this.snapshot, item.cheeseId, item.stars, this.ageDays(item), channel === 'retail' && !this.hasShop ? 'wholesale' : channel, this.negotiation, n, this.saleAging(item));
    return settleSaleAmount(raw, this.state.market.saleRemainder).amount;
  }

  wheyPrice(): number {
    return wheyPricePerGallon(this.snapshot) * (1 + (this.hasSkill('market_negotiation_1') ? MARKET.NEGOTIATION_PER_SKILL : 0));
  }

  sell(inventoryIndex: number, qty: number, channel: Channel): void {
    if (!Number.isFinite(qty) || qty <= 0) return;
    const s = this.state;
    const item = s.inventory[inventoryIndex];
    if (!item) return;
    if (channel === 'retail' && !this.hasShop) channel = 'wholesale';

    if (item.kind === 'cheese') {
      const n = Math.min(Math.floor(qty), item.qty);
      if (n <= 0) return;
      const def = CHEESES[item.cheeseId];
      const raw = quoteCheeseSale(s, this.snapshot, item.cheeseId, item.stars, this.ageDays(item), channel, this.negotiation, n, this.saleAging(item));
      const settled = settleSaleAmount(raw, s.market.saleRemainder);
      const total = settled.amount;
      s.market.saleRemainder = settled.remainder;
      s.market.soldThisWeek[def.marketKey] = (s.market.soldThisWeek[def.marketKey] ?? 0) + n;
      const gems = rollGems(item.stars, def.rarity, n, this.rng);
      item.qty -= n;
      if (item.qty <= 0) s.inventory.splice(inventoryIndex, 1);
      s.gold = Math.round((s.gold + total) * 100) / 100;
      s.gems += gems;
      s.stats.unitsSold += n;
      s.stats.goldEarned += total;
      if (!item.ownerId || item.ownerId === 'player') s.learning.soldCheese = true;
      s.stats.gemsFound += gems;
      this.toast(`Sold ${n} × ${item.stars}★ ${def.name} for ${total.toFixed(2)} gold.`, 'good');
      if (gems > 0) this.toast(`A buyer tipped you ${gems} cosmic gem${gems === 1 ? '' : 's'}!`, 'gem');
      this.addXp('sell', n + n * item.stars);
      this.tutorialComplete('sell');
    } else if (item.kind === 'whey') {
      const gal = Math.min(qty, item.qty);
      if (gal <= 0) return;
      const total = Math.round(gal * this.wheyPrice() * 100) / 100;
      item.qty = Math.round((item.qty - gal) * 10) / 10;
      if (item.qty <= 0) s.inventory.splice(inventoryIndex, 1);
      s.gold = Math.round((s.gold + total) * 100) / 100;
      s.stats.goldEarned += total;
      this.toast(`Sold ${gal} gal of whey for ${total.toFixed(2)} gold.`, 'good');
      this.addXp('sell', Math.ceil(gal));
    }
    this.emit('change', undefined);
  }

  eat(inventoryIndex: number): void {
    const s = this.state;
    const item = s.inventory[inventoryIndex];
    if (!item) return;
    if (item.kind === 'cheese') {
      const def = CHEESES[item.cheeseId];
      const amount = def.energyRestore.base + def.energyRestore.perStar * item.stars;
      item.qty--;
      if (item.qty <= 0) s.inventory.splice(inventoryIndex, 1);
      this.restoreEnergy(amount);
      this.toast(`Ate a ${item.stars}★ ${def.name}. +${amount} energy.`, 'good');
    } else if (item.kind === 'whey') {
      const gal = Math.min(1, item.qty);
      item.qty = Math.round((item.qty - gal) * 10) / 10;
      if (item.qty <= 0) s.inventory.splice(inventoryIndex, 1);
      this.restoreEnergy(ENERGY.EAT_WHEY);
      this.toast(`Drank a gallon of whey. +${ENERGY.EAT_WHEY} energy.`, 'info');
    }
    this.emit('change', undefined);
  }

  private restoreEnergy(amount: number): void {
    this.state.energy = Math.min(ENERGY.MAX, this.state.energy + amount);
  }

  // ------------------------------------------------------ supplies/skills

  canBuySupply(id: SupplyId): string | null {
    const def = SUPPLIES[id];
    if (def.requiresSkill && !this.hasSkill(def.requiresSkill)) return `Requires ${SKILL_BY_ID[def.requiresSkill].name}.`;
    return null;
  }

  buySupply(id: SupplyId, qty: number): boolean {
    if (!Number.isSafeInteger(qty) || qty <= 0) return false;
    const def = SUPPLIES[id];
    const blocked = this.canBuySupply(id);
    if (blocked) {
      this.toast(blocked, 'bad');
      return false;
    }
    const cost = Math.round(def.price * qty * 100) / 100;
    if (this.state.gold < cost) {
      this.toast(`Not enough gold (${cost.toFixed(2)} needed).`, 'bad');
      return false;
    }
    this.state.gold = Math.round((this.state.gold - cost) * 100) / 100;
    this.state.supplies[id] += qty;
    this.toast(`Bought ${qty} × ${def.label.toLowerCase()} for ${cost.toFixed(2)} gold.`, 'info');
    this.emit('change', undefined);
    return true;
  }

  repairVat(): boolean {
    if (this.state.gold < VAT.REPAIR_COST) {
      this.toast(`Repair costs ${VAT.REPAIR_COST} gold.`, 'bad');
      return false;
    }
    this.state.gold -= VAT.REPAIR_COST;
    this.state.vat.durability = VAT.DURABILITY_MAX;
    this.toast('Vat repaired to full durability.', 'good');
    this.emit('change', undefined);
    return true;
  }

  canBuySkill(id: string): string | null {
    const node = SKILL_BY_ID[id];
    if (!node) return 'Unknown skill.';
    if (this.hasSkill(id)) return 'Already owned.';
    if (!node.functional) return `Arrives in ${node.laterBuild ?? 'a later build'}.`;
    for (const r of node.requires) if (!this.hasSkill(r)) return `Requires ${SKILL_BY_ID[r]?.name ?? r}.`;
    if (id === 'employees' && (!this.state.learning.madeCheese || !this.state.learning.agedCheese || !this.state.learning.soldCheese)) return 'Make, age, and sell cheese yourself before unlocking employees.';
    if (this.state[node.currency] < node.cost) return `Costs ${node.cost} ${node.currency}.`;
    return null;
  }

  buySkill(id: string): boolean {
    const blocked = this.canBuySkill(id);
    if (blocked) {
      this.toast(blocked, 'bad');
      return false;
    }
    const node = SKILL_BY_ID[id];
    this.state[node.currency] -= node.cost;
    this.state.skills.push(id);
    this.save();
    this.toast(`Unlocked ${node.name}!`, 'good');
    if (node.affectsRooms) this.emit('room', this.state.room);
    this.emit('change', undefined);
    return true;
  }

  coinUpgradeBlock(id: CoinUpgradeId): string | null {
    const u = COIN_UPGRADE_BY_ID[id];
    if (!u) return 'Unknown coin upgrade.';
    const level = upgradeLevel(this.state, id);
    if (level >= 15) return 'Already at maximum level.';
    if (u.requiresSkill && !this.hasSkill(u.requiresSkill)) return `Requires ${SKILL_BY_ID[u.requiresSkill]?.name ?? u.requiresSkill}.`;
    if (id === 'craft_notebook' && !this.state.learning.madeCheese) return 'Complete your first cheese first.';
    if (id === 'crew_practice' && this.state.staff.employees.length === 0) return 'Hire your first employee first.';
    const cost = upgradeCost(id, level + 1);
    if (this.state.gold < cost) return `Costs ${cost} coins.`;
    return null;
  }

  buyCoinUpgrade(id: CoinUpgradeId): boolean {
    const blocked = this.coinUpgradeBlock(id);
    if (blocked) { this.toast(blocked, 'bad'); return false; }
    const level = upgradeLevel(this.state, id);
    const cost = upgradeCost(id, level + 1);
    this.state.gold = Math.round((this.state.gold - cost) * 100) / 100;
    this.state.coinUpgradeLevels[id] = level + 1;
    this.save();
    this.toast(`${COIN_UPGRADE_BY_ID[id].name} reached level ${level + 1}.`, 'good');
    this.emit('change', undefined);
    return true;
  }

  pinUpgrade(id: CoinUpgradeId | null): boolean {
    if (id !== null && !COIN_UPGRADE_BY_ID[id]) return false;
    this.state.pinnedUpgradeId = id;
    this.save();
    this.emit('change', undefined);
    return true;
  }

  // ---------------------------------------------------------------- day

  get canEndDay(): boolean {
    return this.isWorkDayOver;
  }

  endDay(forced = false): void {
    const s = this.state;
    if (!forced && !this.canEndDay) {
      this.toast('The work day is not over yet.', 'info');
      return;
    }
    const overtimeHours = Math.floor(Math.max(0, s.minuteOfDay - this.workDayEnd) / 60);

    // Staff finish the day's work, get paid, and learn
    if (s.staff.employees.length) {
      this.payWagesAndRollAttendance();
      const training = Staff.applyTraining(s, (t) => this.isMastered(t));
      for (const t of training.fromBank) this.toast(`${t.name} learned ${t.amount.toFixed(0)} ${TASK[t.task].label.toLowerCase()} XP from your training bank.`, 'info');
      for (const t of training.fromTrainer) this.toast(`${t.name} picked up ${t.amount.toFixed(0)} ${TASK[t.task].label.toLowerCase()} XP from the trainer.`, 'info');
    }

    // Cheese ages; spoiled cheese is discarded
    const kept = [];
    for (const item of s.inventory) {
      if (item.kind === 'cheese') {
        const def = CHEESES[item.cheeseId];
        const ageTomorrow = s.day + 1 - item.madeDay;
        const limit = def.aging.ageable ? def.aging.shelfDays : def.aging.spoilAfterDays;
        if (ageTomorrow >= limit) {
          this.toast(`${item.qty} × ${def.name} spoiled overnight.`, 'bad');
          continue;
        }
      }
      kept.push(item);
    }
    s.inventory = kept;

    // A batch left in the vat spoils; the table and tub keep working
    if (s.vat.batch) {
      this.toast('The batch left in the vat spoiled overnight.', 'bad');
      this.wearVat(s.vat.batch);
      s.vat.batch = null;
      this.emit('batchStage', null);
    }

    // The cellar ages
    if (this.hasSkill('cellar')) {
      const report = Cellar.processCellarDay(s, this.rng);
      for (const sp of report.spoiled) this.toast(`${sp.qty} × ${CHEESES[sp.cheeseId].name} spoiled on the racks.`, 'bad');
      if (report.missedWashes > 0) this.toast(`${report.missedWashes} rack${report.missedWashes === 1 ? '' : 's'} went unwashed too long (−0.1 value).`, 'bad');
      for (const r of report.ready) this.toast(`${r.qty} × ${CHEESES[r.cheeseId].name} reached minimum age. Value climbs from here.`, 'good');
      if (s.cellar.racks.length && report.speed === 0) this.toast('Cellar conditions are poor. Aging stalled.', 'bad');
    }

    s.day++;
    s.minuteOfDay = TIME.DAY_START;
    s.energy = Math.max(0, ENERGY.MAX - overtimeHours * ENERGY.OVERTIME_PENALTY_PER_HOUR);
    s.bessie.fedToday = false;
    s.bessie.feedType = null;
    s.bessie.lastMilkedMinute = null;
    s.bessie.milkingsToday = 0;
    s.stats.daysPlayed++;

    if ((s.day - 1) % TIME.DAYS_PER_WEEK === 0) {
      s.market.weekIndex++;
      s.market.soldThisWeek = {};
      this.toast('A new market week begins. Demand has recovered.', 'info');
    }

    this.toast(`Day ${s.day}. ${overtimeHours > 0 ? `Overtime cost ${overtimeHours * ENERGY.OVERTIME_PENALTY_PER_HOUR} energy.` : 'Well rested.'}`, 'info');
    this.save();
    this.emit('day', s.day);
    this.emit('change', undefined);
  }

  // --------------------------------------------------------------- misc

  setRoom(room: RoomId, tile: { x: number; y: number }): void {
    this.state.room = room;
    this.state.playerTile = { ...tile };
    this.state.playerPosition = { x: (tile.x + .5) * 64, y: (tile.y + .5) * 64 };
    this.emit('room', room);
  }

  setPlayerTile(tile: { x: number; y: number }): void {
    this.state.playerTile = { ...tile };
  }

  toast(text: string, kind: ToastKind = 'info'): void {
    this.emit('toast', { text, kind });
  }

  tutorialComplete(task: TaskId): void {
    const t = this.state.tutorial;
    if (t.done) return;
    if (!t.completed.includes(task)) t.completed.push(task);
    if (task === 'sell') t.done = true;
  }

  save(): void {
    saveState(this.state);
  }

  reset(): void {
    clearSave();
    this.state = createNewState();
    this.state.market.snapshotDate = this.snapshot.generatedAt;
    this.quiz = null;
    this.emit('batchStage', null);
    this.emit('room', this.state.room);
    this.emit('change', undefined);
  }

  replaceState(next: SaveState): void {
    this.state = next;
    next.playerMovingTo = null;
    for (const employee of next.staff.employees) ensureStaffPosition(next, employee, true);
    this.quiz = null;
    this.save();
    this.emit('batchStage', next.vat.batch?.stage ?? null);
    this.emit('room', next.room);
    this.emit('change', undefined);
  }
}

/** Which skill each production stage exercises. */
export function stageTask(stage: B.Stage): TaskId {
  switch (stage) {
    case 'pasteurize':
    case 'heat':
      return 'pasteurize';
    case 'curdle':
    case 'acidify':
      return 'curdle';
    case 'culture':
      return 'culture';
    case 'rennet':
      return 'rennet';
    case 'cut':
    case 'skim':
    case 'drain':
    case 'mold':
      return 'cut';
    case 'stretch':
      return 'stretch';
    case 'brine':
      return 'brine';
    case 'rack':
      return 'cellar';
    case 'bag':
    case 'tub':
      return 'bag';
    default:
      return 'bag';
  }
}

export const ALL_TASKS = TASK_IDS;
